import React, { useState, useEffect } from 'react';
import * as teamsJs from "@microsoft/teams-js";
import {
  Button,
  Card,
  CardHeader,
  Text,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  TabList,
  Tab,
  TabValue,
} from '@fluentui/react-components';
import {
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  Info24Regular,
  ChevronRight20Regular,
  ChevronDown20Regular,
  Play24Regular,
  ArrowClockwise24Regular,
  Agents24Regular,
  Bug24Regular,
  Code24Regular
} from '@fluentui/react-icons';
import { GraphUser } from './types';
import { telemetryService } from './services/telemetry.service';


const useStyles = makeStyles({
  container: {
    ...shorthands.padding('20px'),
    maxWidth: '1200px',
    ...shorthands.margin('0 auto'),
  },
  header: {
    marginBottom: '20px',
  },
  card: {
    marginBottom: '16px',
  },
  buttonGroup: {
    display: 'flex',
    ...shorthands.gap('10px'),
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  resultBox: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('8px'),
    fontFamily: 'monospace',
    fontSize: '12px',
    overflowX: 'auto',
    marginTop: '12px',
  },
  success: {
    color: tokens.colorPaletteGreenForeground1,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
  info: {
    color: tokens.colorPaletteBlueForeground2,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    marginBottom: '8px',
  },
  pollingSection: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('8px'),
    marginBottom: '16px',
  },
  pollingButton: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  pollingStatus: {
    marginTop: '12px',
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('6px'),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  consoleOutput: {
    backgroundColor: '#1e1e1e',
    color: '#f8f8f2',
    padding: '15px',
    borderRadius: '5px',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    maxHeight: '300px',
    overflowY: 'auto',
    marginTop: '15px',
  },
  posthogSection: {
    marginTop: '20px',
  },
});

interface TestResult {
  status: 'success' | 'error' | 'info';
  message: string;
  details?: any;
}



export const DebugPage: React.FC = () => {
  const styles = useStyles();
  const [loading, setLoading] = useState(false);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [tokenInfo, setTokenInfo] = useState<TestResult | null>(null);
  const [provisioningStatus, setProvisioningStatus] = useState<TestResult | null>(null);
  const [provisioningTest, setProvisioningTest] = useState<TestResult | null>(null);
  const [approvalCreation, setApprovalCreation] = useState<TestResult | null>(null);

  // Tests On-Behalf-Of
  const [oboTokenInfo, setOboTokenInfo] = useState<TestResult | null>(null);
  const [oboProvisioningStatus, setOboProvisioningStatus] = useState<TestResult | null>(null);
  const [oboProvisioningTest, setOboProvisioningTest] = useState<TestResult | null>(null);
  const [oboApprovalCreation, setOboApprovalCreation] = useState<TestResult | null>(null);
  const [tokenDecoding, setTokenDecoding] = useState<TestResult | null>(null);
  const [graphOboTest, setGraphOboTest] = useState<TestResult | null>(null);

  // État pour le polling manuel
  const [pollingStatus, setPollingStatus] = useState<TestResult | null>(null);
  const [isPolling, setIsPolling] = useState(false);


  const [activeTab, setActiveTab] = useState<TabValue>('approvals');
  
  // État pour le groupe de sécurité autorisé
  const [allowedSecurityGroup, setAllowedSecurityGroup] = useState<GraphUser | null>(null);
  const [loadingSecurityGroup, setLoadingSecurityGroup] = useState(false);

  // Console output state
  const [consoleOutput, setConsoleOutput] = useState<string[]>([
    '🔍 Debug console ready. Select a tab to begin testing.'
  ]);

  // Récupérer le token utilisateur Teams au chargement
  useEffect(() => {
    (async () => {
      try {
        console.log('[Debug] Initializing Teams JS...');
        await teamsJs.app.initialize();
        console.log('[Debug] Teams JS initialized, requesting auth token...');

        const token = await teamsJs.authentication.getAuthToken({
          silent: false,
        });

        setUserToken(token);
        console.log('[Debug] User token obtained:', token.substring(0, 50) + '...');
        console.log('[Debug] Token length:', token.length);
      } catch (error: any) {
        console.error('[Debug] Error getting Teams token:', {
          error: error.message,
          code: error.errorCode,
          fullError: error
        });

        // Essayer d'afficher l'erreur à l'utilisateur
        setOboTokenInfo({
          status: 'error',
          message: 'Impossible de récupérer le token utilisateur',
          details: {
            error: error.message,
            errorCode: error.errorCode,
            hint: 'Vérifiez que l\'application est bien lancée via F5 dans VS Code et ouverte dans Teams'
          }
        });
      }
    })();
  }, []);

  // Override console methods to capture output
  useEffect(() => {
    const originalConsoleLog = console.log;
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.log = function(...args) {
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, null, 2);
        }
        return String(arg);
      }).join(' ');
      addToConsole(message);
      originalConsoleLog.apply(console, args);
    };

    console.error = function(...args) {
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, null, 2);
        }
        return String(arg);
      }).join(' ');
      addToConsole(`❌ ${message}`);
      originalConsoleError.apply(console, args);
    };

    console.warn = function(...args) {
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, null, 2);
        }
        return String(arg);
      }).join(' ');
      addToConsole(`⚠️ ${message}`);
      originalConsoleWarn.apply(console, args);
    };

    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);

  const addToConsole = (message: string) => {
    setConsoleOutput(prev => [...prev, message]);
  };

  const clearConsole = () => {
    setConsoleOutput(['Console cleared.']);
  };

  const testTokenInfo = async () => {
    setLoading(true);
    setTokenInfo(null);
    try {
      const response = await fetch('/api/debug/token-info');
      const data = await response.json();

      if (response.ok) {
        setTokenInfo({
          status: 'success',
          message: 'Token récupéré avec succès',
          details: data,
        });
      } else {
        setTokenInfo({
          status: 'error',
          message: 'Erreur lors de la récupération du token',
          details: data,
        });
      }
    } catch (error: any) {
      setTokenInfo({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour déclencher le polling manuellement
  const triggerManualPolling = async () => {
    setIsPolling(true);
    setPollingStatus(null);
    try {
      const response = await fetch('/api/debug/trigger-polling', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        setPollingStatus({
          status: 'success',
          message: 'Polling déclenché avec succès',
          details: data,
        });
      } else {
        setPollingStatus({
          status: 'error',
          message: 'Erreur lors du déclenchement du polling',
          details: data,
        });
      }
    } catch (error: any) {
      setPollingStatus({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setIsPolling(false);
    }
  };

  const testProvisioningStatus = async () => {
    setLoading(true);
    setProvisioningStatus(null);
    try {
      const response = await fetch('/api/debug/approval-status');
      const data = await response.json();

      if (response.ok) {
        setProvisioningStatus({
          status: 'success',
          message: 'Statut de provisionnement récupéré',
          details: data,
        });
      } else {
        setProvisioningStatus({
          status: 'error',
          message: 'Erreur lors de la vérification du statut',
          details: data,
        });
      }
    } catch (error: any) {
      setProvisioningStatus({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const testProvisioning = async () => {
    setLoading(true);
    setProvisioningTest(null);
    try {
      const response = await fetch('/api/debug/approval-provision', { method: 'POST' });
      const data = await response.json();

      if (response.ok) {
        setProvisioningTest({
          status: 'success',
          message: 'Provisionnement réussi',
          details: data,
        });
      } else {
        setProvisioningTest({
          status: 'error',
          message: 'Erreur lors du provisionnement',
          details: data,
        });
      }
    } catch (error: any) {
      setProvisioningTest({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const testApprovalCreation = async () => {
    setLoading(true);
    setApprovalCreation(null);
    try {
      const response = await fetch('/api/debug/create-test-approval', { method: 'POST' });
      const data = await response.json();

      if (response.ok) {
        setApprovalCreation({
          status: 'success',
          message: 'Approbation de test créée',
          details: data,
        });
      } else {
        setApprovalCreation({
          status: 'error',
          message: 'Erreur lors de la création de l\'approbation',
          details: data,
        });
      }
    } catch (error: any) {
      setApprovalCreation({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  // Tests On-Behalf-Of
  const testOboTokenInfo = async () => {
    if (!userToken) {
      setOboTokenInfo({
        status: 'error',
        message: 'Token utilisateur non disponible',
        details: { hint: 'L\'application doit être exécutée dans Teams' },
      });
      return;
    }

    setLoading(true);
    setOboTokenInfo(null);
    try {
      const response = await fetch('/api/debug/obo-token-info', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        setOboTokenInfo({
          status: 'success',
          message: 'Token OBO récupéré avec succès',
          details: data,
        });
      } else {
        setOboTokenInfo({
          status: 'error',
          message: 'Erreur lors de la récupération du token OBO',
          details: data,
        });
      }
    } catch (error: any) {
      setOboTokenInfo({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const testOboProvisioningStatus = async () => {
    if (!userToken) {
      setOboProvisioningStatus({
        status: 'error',
        message: 'Token utilisateur non disponible',
      });
      return;
    }

    setLoading(true);
    setOboProvisioningStatus(null);
    try {
      const response = await fetch('/api/debug/obo-approval-status', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        setOboProvisioningStatus({
          status: 'success',
          message: 'Statut de provisionnement récupéré (OBO)',
          details: data,
        });
      } else {
        setOboProvisioningStatus({
          status: 'error',
          message: 'Erreur lors de la vérification du statut (OBO)',
          details: data,
        });
      }
    } catch (error: any) {
      setOboProvisioningStatus({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const testOboProvisioning = async () => {
    if (!userToken) {
      setOboProvisioningTest({
        status: 'error',
        message: 'Token utilisateur non disponible',
      });
      return;
    }

    setLoading(true);
    setOboProvisioningTest(null);
    try {
      const response = await fetch('/api/debug/obo-approval-provision', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        setOboProvisioningTest({
          status: 'success',
          message: 'Provisionnement réussi (OBO)',
          details: data,
        });
      } else {
        setOboProvisioningTest({
          status: 'error',
          message: 'Erreur lors du provisionnement (OBO)',
          details: data,
        });
      }
    } catch (error: any) {
      setOboProvisioningTest({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const testOboApprovalCreation = async () => {
    if (!userToken) {
      setOboApprovalCreation({
        status: 'error',
        message: 'Token utilisateur non disponible',
      });
      return;
    }

    setLoading(true);
    setOboApprovalCreation(null);
    try {
      const response = await fetch('/api/debug/obo-create-test-approval', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        setOboApprovalCreation({
          status: 'success',
          message: 'Approbation de test créée (OBO)',
          details: data,
        });
      } else {
        setOboApprovalCreation({
          status: 'error',
          message: 'Erreur lors de la création de l\'approbation (OBO)',
          details: data,
        });
      }
    } catch (error: any) {
      setOboApprovalCreation({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const testTokenDecoding = async () => {
    if (!userToken) {
      setTokenDecoding({
        status: 'error',
        message: 'Token utilisateur non disponible',
      });
      return;
    }

    setLoading(true);
    setTokenDecoding(null);
    try {
      const response = await fetch('/api/debug/decode-tokens', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        setTokenDecoding({
          status: 'success',
          message: 'Tokens décodés avec succès',
          details: data,
        });
      } else {
        setTokenDecoding({
          status: 'error',
          message: 'Erreur lors du décodage des tokens',
          details: data,
        });
      }
    } catch (error: any) {
      setTokenDecoding({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const testGraphObo = async () => {
    if (!userToken) {
      setGraphOboTest({
        status: 'error',
        message: 'Token utilisateur non disponible',
      });
      return;
    }

    setLoading(true);
    setGraphOboTest(null);
    try {
      const response = await fetch('/api/debug/test-graph-obo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        setGraphOboTest({
          status: 'success',
          message: 'Tests Graph API OBO terminés',
          details: data,
        });
      } else {
        setGraphOboTest({
          status: 'error',
          message: 'Erreur lors des tests Graph API OBO',
          details: data,
        });
      }
    } catch (error: any) {
      setGraphOboTest({
        status: 'error',
        message: error.message,
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllowedSecurityGroup = async () => {
    setLoadingSecurityGroup(true);
    try {
      const response = await fetch('/api/admin/allowed-security-group');
      const data = await response.json();

      if (response.ok) {
        setAllowedSecurityGroup(data.data);
      } else {
        setAllowedSecurityGroup(null);
      }
    } catch (error: any) {
      console.error('Error loading allowed security group:', error);
      setAllowedSecurityGroup(null);
    } finally {
      setLoadingSecurityGroup(false);
    }
  };



  const renderResult = (result: TestResult | null) => {
    if (!result) return null;

    const Icon = result.status === 'success' ? CheckmarkCircle24Regular :
                 result.status === 'error' ? DismissCircle24Regular :
                 Info24Regular;

    const colorClass = result.status === 'success' ? styles.success :
                      result.status === 'error' ? styles.error :
                      styles.info;

    return (
      <div>
        <div className={styles.statusRow}>
          <Icon className={colorClass} />
          <Text weight="semibold" className={colorClass}>
            {result.message}
          </Text>
        </div>
        {result.details && (
          <pre className={styles.resultBox}>
            {JSON.stringify(result.details, null, 2)}
          </pre>
        )}
      </div>
    );
  };



  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text as="h1" size={800} weight="bold">
          🔧 Debug & Analytics Dashboard
        </Text>
        <Text>
          Comprehensive debugging and analytics testing for the Teams application
        </Text>
      </div>

      {/* App Host Information */}
      <Card className={styles.card}>
        <CardHeader
          header={<Text weight="bold">📡 App Host Information</Text>}
        />
        <div style={{ padding: '16px' }}>
          <div className={styles.statusRow}>
            <Info24Regular className={styles.info} />
            <Text weight="semibold" className={styles.info}>
              Your app is running in: <strong>{typeof window !== 'undefined' && (window as any).__appHostName || 'Unknown'}</strong>
            </Text>
          </div>
        </div>
      </Card>

      {/* Navigation Tabs */}
      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as TabValue)}
        appearance="subtle"
        size="medium"
      >
        <Tab icon={<Code24Regular />} value="approvals">Approvals Debug</Tab>
        <Tab icon={<Bug24Regular />} value="console">Debug Console</Tab>
      </TabList>

      {/* Approvals Debug Section */}
  {activeTab === 'approvals' && (
    <>
      {/* Section Groupe de Sécurité Autorisé */}
      <div className={styles.pollingSection}>
        <Text as="h2" size={600} weight="bold" className="section-header">
          🔒 Groupe de Sécurité Autorisé
        </Text>
        <Text style={{ marginBottom: '16px' }}>
          Vérifiez la configuration du groupe de sécurité autorisé pour ce tenant
        </Text>
        <Button
          appearance="primary"
          onClick={loadAllowedSecurityGroup}
          disabled={loadingSecurityGroup}
          className={styles.pollingButton}
          icon={loadingSecurityGroup ? <Spinner size="tiny" /> : <ArrowClockwise24Regular />}
        >
          {loadingSecurityGroup ? 'Chargement...' : 'Charger le Groupe de Sécurité'}
        </Button>
        {allowedSecurityGroup && (
          <div className={styles.pollingStatus}>
            <div className={styles.statusRow}>
              <CheckmarkCircle24Regular className={styles.success} />
              <Text weight="semibold" className={styles.success}>
                Groupe de sécurité configuré
              </Text>
            </div>
            <pre className={styles.resultBox}>
              {JSON.stringify({
                displayName: allowedSecurityGroup.displayName,
                mail: allowedSecurityGroup.mail,
                userPrincipalName: allowedSecurityGroup.userPrincipalName,
                id: allowedSecurityGroup.id
              }, null, 2)}
            </pre>
          </div>
        )}
        {!allowedSecurityGroup && !loadingSecurityGroup && (
          <div className={styles.pollingStatus}>
            <div className={styles.statusRow}>
              <Info24Regular className={styles.info} />
              <Text weight="semibold" className={styles.info}>
                Aucun groupe de sécurité configuré
              </Text>
            </div>
            <pre className={styles.resultBox}>
              Aucun groupe de sécurité autorisé n'est configuré pour ce tenant.
            </pre>
          </div>
        )}
      </div>

      {/* Section Polling Manuel */}
      <div className={styles.pollingSection}>
            <Text as="h2" size={600} weight="bold" style={{ marginBottom: '16px' }}>
              🔄 Manual Polling
            </Text>
            <Text style={{ marginBottom: '16px' }}>
              Manually trigger approvals synchronization between Teams and SharePoint
            </Text>
            <Button
              appearance="primary"
              onClick={triggerManualPolling}
              disabled={isPolling || !userToken}
              className={styles.pollingButton}
              icon={isPolling ? <Spinner size="tiny" /> : <ArrowClockwise24Regular />}
            >
              {isPolling ? 'Polling in progress...' : 'Trigger Polling'}
            </Button>
            {pollingStatus && (
              <div className={styles.pollingStatus}>
                {renderResult(pollingStatus)}
              </div>
            )}
          </div>

          {/* Tests with accordions - collapsed by default */}
          <Accordion multiple defaultOpenItems={[]}>
            <AccordionItem value="token-tests">
              <AccordionHeader expandIconPosition="end">
                <Text as="h2" size={600} weight="bold" style={{ color: tokens.colorPaletteBlueForeground2 }}>
                  🔑 Token & Provisioning Tests
                </Text>
              </AccordionHeader>
              <AccordionPanel>
                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">1. Check Access Token</Text>}
                    description="Verify permissions in Microsoft Graph access token"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testTokenInfo} disabled={loading}>
                      {loading ? <Spinner size="tiny" /> : 'Test Token'}
                    </Button>
                  </div>
                  {renderResult(tokenInfo)}
                </Card>

                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">2. Check Provisioning Status</Text>}
                    description="Check if Approvals solution is provisioned for the tenant"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testProvisioningStatus} disabled={loading}>
                      {loading ? <Spinner size="tiny" /> : 'Check Status'}
                    </Button>
                  </div>
                  {renderResult(provisioningStatus)}
                </Card>

                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">3. Provision Approvals Solution</Text>}
                    description="Provision the approvals solution for the tenant (required once)"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testProvisioning} disabled={loading}>
                      {loading ? <Spinner size="tiny" /> : 'Provision'}
                    </Button>
                  </div>
                  {renderResult(provisioningTest)}
                </Card>

                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">4. Create Test Approval</Text>}
                    description="Create a test approval to verify full functionality"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testApprovalCreation} disabled={loading}>
                      {loading ? <Spinner size="tiny" /> : 'Create Test Approval'}
                    </Button>
                  </div>
                  {renderResult(approvalCreation)}
                </Card>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem value="obo-tests">
              <AccordionHeader expandIconPosition="end">
                <Text as="h2" size={600} weight="bold" style={{ color: tokens.colorPaletteBlueForeground2 }}>
                  ✨ On-Behalf-Of Tests (Recommended)
                </Text>
              </AccordionHeader>
              <AccordionPanel>
                <Text style={{ marginBottom: '16px' }}>
                  {userToken ? '✅ User token detected' : '⚠️ User token not available (local mode)'}
                </Text>

                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">5. Test On-Behalf-Of Flow</Text>}
                    description="Get a delegated token from Teams user token"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testOboTokenInfo} disabled={loading || !userToken}>
                      {loading ? <Spinner size="tiny" /> : 'Test OBO Token'}
                    </Button>
                  </div>
                  {renderResult(oboTokenInfo)}
                </Card>

                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">6. Check Status with OBO</Text>}
                    description="Check provisioning with delegated token"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testOboProvisioningStatus} disabled={loading || !userToken}>
                      {loading ? <Spinner size="tiny" /> : 'Check with OBO'}
                    </Button>
                  </div>
                  {renderResult(oboProvisioningStatus)}
                </Card>

                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">7. Provision with OBO</Text>}
                    description="Provision solution with delegated token"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testOboProvisioning} disabled={loading || !userToken}>
                      {loading ? <Spinner size="tiny" /> : 'Provision with OBO'}
                    </Button>
                  </div>
                  {renderResult(oboProvisioningTest)}
                </Card>

                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">8. Create Approval with OBO</Text>}
                    description="Create test approval with delegated token"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testOboApprovalCreation} disabled={loading || !userToken}>
                      {loading ? <Spinner size="tiny" /> : 'Create with OBO'}
                    </Button>
                  </div>
                  {renderResult(oboApprovalCreation)}
                </Card>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem value="token-analysis">
              <AccordionHeader expandIconPosition="end">
                <Text as="h2" size={600} weight="bold" style={{ color: tokens.colorPaletteRedForeground1 }}>
                  🔐 Token Analysis
                </Text>
              </AccordionHeader>
              <AccordionPanel>
                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">9. Decode and Analyze Tokens</Text>}
                    description="Display detailed content of Teams user token and OBO token to verify permissions"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testTokenDecoding} disabled={loading || !userToken}>
                      {loading ? <Spinner size="tiny" /> : 'Analyze Tokens'}
                    </Button>
                  </div>
                  {renderResult(tokenDecoding)}
                </Card>

                <Card className={styles.card}>
                  <CardHeader
                    header={<Text weight="semibold">10. Complete Graph API Tests with OBO</Text>}
                    description="Run a series of tests to check which Graph API endpoints work with OBO token"
                  />
                  <div className={styles.buttonGroup}>
                    <Button appearance="primary" onClick={testGraphObo} disabled={loading || !userToken}>
                      {loading ? <Spinner size="tiny" /> : 'Test Graph API OBO'}
                    </Button>
                  </div>
                  {renderResult(graphOboTest)}
                </Card>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

          <Card className={styles.card} appearance="filled">
            <CardHeader
              header={<Text weight="semibold">ℹ️ Important Information</Text>}
            />
            <Text>
              <strong>Required Permissions:</strong>
              <ul>
                <li>ApprovalSolution.ReadWrite (Delegated) - To create and manage approvals</li>
                <li>The token must be a delegated token (with user context), not an application token</li>
              </ul>
              <strong>On-Behalf-Of Solution:</strong>
              <ul>
                <li>Uses Teams user token to get a delegated Graph API token</li>
                <li>Compatible with Approvals API</li>
                <li>Test sections 5-8 above to validate the flow</li>
              </ul>
            </Text>
          </Card>
        </>
      )}



      {/* Debug Console Section */}
      {activeTab === 'console' && (
        <div className={styles.posthogSection}>
          <Text as="h2" size={600} weight="bold" style={{ marginBottom: '16px' }}>
            🐞 Debug Console
          </Text>
          <Text style={{ marginBottom: '20px' }}>
            Real-time console output for debugging
          </Text>

          <div className={styles.buttonGroup}>
            <Button
              appearance="primary"
              onClick={clearConsole}
              icon={<DismissCircle24Regular />}
            >
              Clear Console
            </Button>
          </div>

          <div className={styles.consoleOutput}>
            {consoleOutput.map((line, index) => (
              <div key={index}>
                {line.includes('❌') && (
                  <span style={{ color: '#ff6b6b' }}>{line}</span>
                )}
                {line.includes('⚠️') && (
                  <span style={{ color: '#ffd43b' }}>{line}</span>
                )}
                {line.includes('✅') && (
                  <span style={{ color: '#51cf66' }}>{line}</span>
                )}
                {!line.includes('❌') && !line.includes('⚠️') && !line.includes('✅') && (
                  <span style={{ color: '#f8f8f2' }}>{line}</span>
                )}
              </div>
            ))}
          </div>

          <Card className={styles.card} appearance="filled" style={{ marginTop: '20px' }}>
            <CardHeader
              header={<Text weight="semibold">💡 Console Tips</Text>}
            />
            <Text>
              <ul>
                <li>All console.log, console.error, and console.warn calls are captured here</li>
                <li>Errors are highlighted in red (❌)</li>
                <li>Warnings are highlighted in yellow (⚠️)</li>
                <li>Success messages are highlighted in green (✅)</li>
                <li>Use the "Clear Console" button to reset the output</li>
                <li>Check browser developer tools for additional debugging information</li>
              </ul>
            </Text>
          </Card>
        </div>
      )}
    </div>
  );
};

// Export for backward compatibility
export const DebugApprovalsPage = DebugPage;
